/**
 * my-requests.ts
 * The Solicitudes tab lists everything the worker asked for in one list
 * (owner, 2026-11-04: Campo's "Servicios Solicitados" and "Pedidos de
 * repuestos" were history-only screens with no way to ask; "si necesitas
 * alquilar una bobcat no es compra, es alquilar"). Three sources, each read
 * only with its own guard:
 *
 * - absences — GET /rrhh/self/calendar events (self.legajo.leer)
 * - services — GET /servicios/partner/service-order-requests (locx.servicio.crear):
 *   rentals, trainings, any service asked of a contractor
 * - purchases — GET /procurement/my-cases (requi.solicitud.leer-propias):
 *   repuestos and materials
 *
 * Each becomes a MyRequest row: one title, one subtitle, a state badge and
 * whether it is still open ("En curso") or closed ("Cerradas").
 */
import type { BadgeVariant } from '@suite/ui';
import type { SelfCalendarEventResponse } from '@suite/contracts-rrhh';
import type { MySentServiceOrderRequestItem, ServiceOrderRequestStatus } from '@suite/contracts-servicios';
import type { CaseStatus, MyCaseResponse } from '@suite/contracts-compras';
import { ABSENCE_STATE_LABELS, ABSENCE_STATE_VARIANTS, absenceDateRange, absenceDayCount, absenceTypeName } from './absence-requests.js';
import { localIsoDate } from './hoy-feed.js';

/** "9 nov" — same wording as the absences' dates. */
function dayLabel(iso: string): string {
  return absenceDateRange({ startDate: iso, endDate: iso });
}

export type MyRequestKind = 'ausencia' | 'servicio' | 'compra';

export interface MyRequest {
  readonly key: string;
  readonly kind: MyRequestKind;
  readonly title: string;
  readonly subtitle: string;
  readonly notes: string | null;
  readonly open: boolean;
  /** ISO date the list sorts by: the absence's first day, else the day it was asked. */
  readonly date: string;
  readonly stateLabel: string;
  readonly stateVariant: BadgeVariant;
}

export const ABSENCE_REQUESTS_CAPABILITY = 'self.legajo.leer';
export const SERVICE_REQUESTS_CAPABILITY = 'locx.servicio.crear';
export const PURCHASE_REQUESTS_CAPABILITY = 'requi.solicitud.leer-propias';

const SERVICE_STATES: Record<ServiceOrderRequestStatus, { label: string; variant: BadgeVariant }> = {
  pending: { label: 'Esperando respuesta', variant: 'warning' },
  accepted: { label: 'Aceptada', variant: 'success' },
  rejected: { label: 'Rechazada', variant: 'danger' },
};

/** Same wording as apps/frontend modules/compras/stateLabel.ts (separate apps, no shared package). */
const CASE_STATES: Record<CaseStatus, { label: string; variant: BadgeVariant }> = {
  borrador: { label: 'Borrador', variant: 'neutral' },
  pendiente_aprobacion: { label: 'Pendiente de aprobación', variant: 'warning' },
  requisitada: { label: 'Lista para cotizar', variant: 'neutral' },
  en_cotizacion: { label: 'En cotización', variant: 'primary' },
  adjudicacion_parcial: { label: 'Adjudicación parcial', variant: 'warning' },
  adjudicada: { label: 'Adjudicada', variant: 'success' },
  en_ejecucion: { label: 'En ejecución', variant: 'primary' },
  cerrada: { label: 'Cerrada', variant: 'success' },
  cancelada: { label: 'Cancelada', variant: 'danger' },
};

const CLOSED_CASE_STATES: ReadonlySet<string> = new Set<CaseStatus>(['cerrada', 'cancelada']);

export function fromAbsence(absence: SelfCalendarEventResponse, typeNames: ReadonlyMap<string, string>): MyRequest {
  const days = absenceDayCount(absence);
  return {
    key: `ausencia-${absence.id}`,
    kind: 'ausencia',
    title: absenceTypeName(typeNames, absence),
    subtitle: `${absenceDateRange(absence)} · ${days} ${days === 1 ? 'día' : 'días'}`,
    notes: absence.notes,
    open: absence.currentState === 'SOLICITADA',
    date: absence.startDate,
    stateLabel: ABSENCE_STATE_LABELS[absence.currentState],
    stateVariant: ABSENCE_STATE_VARIANTS[absence.currentState],
  };
}

export function fromServiceRequest(item: MySentServiceOrderRequestItem): MyRequest {
  const date = localIsoDate(new Date(item.requestedAt));
  const state = SERVICE_STATES[item.status];
  return {
    key: `servicio-${item.id}`,
    kind: 'servicio',
    title: item.serviceTypeLabel ?? 'Servicio',
    subtitle: [item.ownerCompanyName, item.wellName ? `Pozo ${item.wellName}` : null, dayLabel(date)].filter(Boolean).join(' · '),
    notes: null,
    open: item.status === 'pending',
    date,
    stateLabel: state.label,
    stateVariant: state.variant,
  };
}

/**
 * A case typed or dictated from the app has no reference, and its derived
 * title only counts items ("1 ítem"): name it by what was asked for instead.
 */
function itemsTitle(items: MyCaseResponse['items']): string | null {
  const first = items[0]?.description?.trim();
  if (!first) return null;
  return items.length > 1 ? `${first} y ${items.length - 1} más` : first;
}

export function fromPurchaseCase(entry: MyCaseResponse): MyRequest {
  const date = localIsoDate(new Date(entry.case.createdAt));
  const state = CASE_STATES[entry.case.status as CaseStatus] ?? { label: entry.case.status, variant: 'neutral' as const };
  const count = entry.items.length;
  return {
    key: `compra-${entry.case.id}`,
    kind: 'compra',
    title: entry.case.reference ?? itemsTitle(entry.items) ?? entry.case.title,
    subtitle: `${entry.case.code} · ${count} ${count === 1 ? 'ítem' : 'ítems'} · ${dayLabel(date)}`,
    notes: null,
    open: !CLOSED_CASE_STATES.has(entry.case.status),
    date,
    stateLabel: state.label,
    stateVariant: state.variant,
  };
}

/** Newest first. */
export function sortRequests(rows: readonly MyRequest[]): MyRequest[] {
  return [...rows].sort((a, b) => b.date.localeCompare(a.date));
}
